import { Controller } from "@hotwired/stimulus"

// Liens et champs d'un en-tête du catalogue (fiche produit, fiche d'un fork) qui portent la page affichée.
// L'en-tête est rendu une fois, au-dessus du défilement continu ; quand on glisse vers une autre page
// (`continuous-pages:change`), ses liens mèneraient encore à celle de l'ouverture. Ce contrôleur les recale sur la
// page courante : sa capacité et son sous-onglet, les deux derniers segments de son adresse
// (…/<code>/tarification/regles, …/<code>/forks/5703/tarification/regles ; cf. Insurance::Catalog::Pages) — ou
// aucun, sur la Présentation (l'adresse nue de la fiche : …/<code>, …/<code>/forks/5703) : les liens mènent alors à la
// Présentation de leur fiche.
//
// Posé par Insurance::Catalog::ForkHeaderComponent (chemin, fournisseur) et ForkPickerComponent (résultats de la
// recherche, champ caché du formulaire).
//
// Valeurs : `current`, la capacité affichée (clé française, posée par le serveur, puis tenue à jour ; vide sur la
// Présentation) ; `tab`, son sous-onglet (adresse : regles, parametrages…), lu dans l'adresse à l'ouverture puis tenu
// à jour.
// Cibles : `link`, un lien vers une fiche (produit ou fork), y compris ceux qui arrivent plus tard dans une frame :
// sa capacité et son sous-onglet deviennent ceux de la page courante (sans sous-onglet, le serveur ouvre le premier
// de la capacité ; sans capacité, la Présentation) ; `param`, un champ qui porte la capacité ; `input`, le champ à
// focaliser (action `focus`, à l'ouverture du menu de recherche).

// Adresse d'une fiche : …/catalogue-produits/<code>[/forks/<n°>][/<capacité>[/<sous-onglet>]]. « forks » (la
// recherche) et « migrations » ne sont ni des codes ni des capacités.
const SHEET_PATH = /^(.*\/catalogue-produits\/(?!(?:forks|migrations)(?:\/|$))[^/]+(?:\/forks\/\d+)?)(?:\/(?!forks(?:\/|$))([^/]+)(?:\/([^/]+))?)?\/?$/

function parseSheet(pathname) {
  const match = SHEET_PATH.exec(pathname)
  return match ? { base: match[1], capability: match[2] ?? "", tab: match[3] ?? "" } : null
}

export default class extends Controller {
  static targets = ["link", "param", "input"]
  static values = { current: String, tab: String }

  connect() {
    // Le serveur ne pose que la capacité : le sous-onglet d'ouverture est celui de l'adresse.
    const sheet = parseSheet(window.location.pathname)
    if (!this.tabValue && sheet && sheet.capability === this.currentValue) this.tabValue = sheet.tab
  }

  follow(event) {
    const url = event.detail?.url
    if (!url) return

    const sheet = parseSheet(new URL(url, window.location.origin).pathname)
    if (!sheet) return

    this.tabValue = sheet.tab
    this.currentValue = sheet.capability
  }

  currentValueChanged() {
    this.retargetAll()
  }

  tabValueChanged() {
    this.retargetAll()
  }

  linkTargetConnected(link) {
    this.retarget(link)
  }

  focus() {
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  retargetAll() {
    this.linkTargets.forEach((link) => this.retarget(link))
    this.paramTargets.forEach((field) => {
      field.value = this.currentValue
    })
  }

  // Capacité vide (la Présentation) : le lien mène à la Présentation de sa fiche.
  retarget(link) {
    const href = link.getAttribute("href")
    if (!href) return

    const url = new URL(href, window.location.origin)
    const sheet = parseSheet(url.pathname)
    if (!sheet) return

    const segments = this.currentValue ? [this.currentValue, this.tabValue] : []
    const pathname = [sheet.base, ...segments].filter(Boolean).join("/")
    if (pathname !== url.pathname) link.setAttribute("href", `${pathname}${url.search}${url.hash}`)
  }
}
